import { Report } from '@mui/icons-material'
import { Alert, Box, Button, Typography } from '@mui/joy'
import { TruckManualModeStage } from '@numeo/types-core'
import { excludeExtensionTruck } from '@numeo/types-main-web-app'
import { useAuth } from 'hooks/auth'
import { useEffect, useMemo, useState } from 'react'
import { handleEmailAdd } from 'utils/email-connect'
import { getUserErrorMessage } from 'utils/get-user-error-message'
import { showError, showSuccess } from 'utils/snackbar'
import axios from 'axios'

// Define the structure of a warning item
type WarningItem = {
    id: string
    message: string
    fixButtonText: string
    fixAction: () => void
}

export const Warnings = () => {
    const { application, setApplication, user } = useAuth()
    const warnings: WarningItem[] = []
    const isTrucksProfilePage = window.location.pathname.includes('/profiles/trucks')
    const [eldErrors, setEldErrors] = useState(application?.integrations?.eldIntegrations?.filter((integration) => integration.lastError) || [])

    useEffect(() => {
        setEldErrors(application?.integrations?.eldIntegrations?.filter((integration) => integration.lastError) || [])
    }, [application])

    const trucksProfileManualCount = useMemo(() => {
        if (!isTrucksProfilePage) return 0

        const trucks = application?.temp?.spotFinder?.trucks || []
        const visibleTrucks = excludeExtensionTruck(trucks)

        return visibleTrucks.filter((truck) => {
            const hasConnectedEld = Boolean(truck.eldIntegration?.vehicleId && (truck.eldIntegration?.integrationId || truck.eldIntegration?.type))
            const stage = truck.manualModeTracking?.stage
            const isManualModeVisible = stage != null && stage !== TruckManualModeStage.RESOLVED
            return !hasConnectedEld && isManualModeVisible
        }).length
    }, [isTrucksProfilePage, application?.temp?.spotFinder?.trucks])

    // Trucks page behavior:
    // 1) If manual truck ELD warning exists, show only this warning.
    // 2) If not, allow regular global warnings below.
    if (isTrucksProfilePage && trucksProfileManualCount > 0) {
        return (
            <Box>
                <Alert
                    color="danger"
                    startDecorator={<Report color="error" />}
                    variant="soft"
                    sx={{
                        alignItems: 'center',
                        width: '100%',
                    }}
                >
                    <Typography>
                        {trucksProfileManualCount} {trucksProfileManualCount === 1 ? 'truck is' : 'trucks are'} running without ELD integration.
                    </Typography>
                    <Button variant="solid" color="danger" size="sm" onClick={() => (window.location.href = '/settings?tab=integrations')}>
                        Connect ELD
                    </Button>
                </Alert>
            </Box>
        )
    }

    // Find all expired Nylas emails in the application
    const expiredNylasEmails = application?.integrations?.nylas?.emails?.filter((email) => email.isExpired) || []

    // Check if any of the user's Nylas emails have expired
    // This handles both the main user and sub-users who added their own emails
    const userExpiredEmails = expiredNylasEmails.filter((expiredEmail) => {
        // Check if this email belongs to the current user's Nylas emails
        return user?.nylasEmails?.some((userEmail) => userEmail.email === expiredEmail.email)
    })

    // Add warnings for each expired email that belongs to this user
    userExpiredEmails.forEach((expiredEmail) => {
        warnings.push({
            id: `expired-email-${expiredEmail.email}`,
            message: `Your email account ${expiredEmail.email} has expired. Please reconnect.`,
            fixButtonText: 'Reconnect Email',
            fixAction: () => {
                handleEmailAdd(
                    (accounts) => {
                        if (application && accounts.length > 0) {
                            // Create a complete deep copy of the entire application object
                            const updatedApplication = JSON.parse(JSON.stringify(application))

                            // Make sure nylas integration exists
                            if (updatedApplication.integrations && updatedApplication.integrations.nylas && updatedApplication.integrations.nylas.emails) {
                                // Find the expired email and update it in the copied structure
                                const emailIndex = updatedApplication.integrations.nylas.emails.findIndex(
                                    (email: { email: string }) => email.email === expiredEmail.email
                                )

                                if (emailIndex !== -1) {
                                    updatedApplication.integrations.nylas.emails[emailIndex].isExpired = false
                                }

                                setApplication(updatedApplication)
                            }
                        }

                        showSuccess('Email account reconnected successfully')
                    },
                    (error) => {
                        showError(getUserErrorMessage(error))
                    },
                    (message, type) => {
                        if (type === 'danger') {
                            showError(message)
                        } else {
                            showSuccess(message)
                        }
                    }
                )
            },
        })
    })

    // Add warnings for ELD provider internal errors
    eldErrors.forEach((errorIntegration) => {
        warnings.push({
            id: `eld-error-${errorIntegration.id}`,
            message: errorIntegration.lastError || 'ELD provider is experiencing internal errors.',
            fixButtonText: 'Clear Error',
            fixAction: async () => {
                try {
                    const response = await axios.post(`/eld/${errorIntegration.id}/clear-error`)
                    setApplication(response.data.application)
                    showSuccess(`Cleared error flag for ${errorIntegration.companyName}`)
                } catch (error) {
                    console.error('Failed to clear ELD error:', error)
                    showError(getUserErrorMessage(error))
                }
            },
        })
    })

    if (warnings.length === 0) return null

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {warnings.map((warning) => (
                <Alert
                    key={warning.id}
                    color="danger"
                    startDecorator={<Report color="error" />}
                    variant="soft"
                    sx={{
                        alignItems: 'center',
                        width: '100%',
                    }}
                >
                    <Typography>{warning.message}</Typography>
                    <Button variant="solid" color="danger" size="sm" onClick={warning.fixAction}>
                        {warning.fixButtonText}
                    </Button>
                </Alert>
            ))}
        </Box>
    )
}
